/**
 * EventForm - Formularz dodawania/edycji wydarzeń
 * wojskaspecjalne.pl
 */

import React, { useState, useEffect } from 'react';
import {
  createEvent,
  updateEvent,
  getEventById,
  getAllAuthors,
  createAuthor,
  uploadEventFile,
  type CreateEventData,
  type Event,
} from '../../lib/eventService';
import type { Author } from '../../types/database.types';
import { useAuth } from '../../contexts/AuthContext';
import './EventForm.css';

interface EventFormProps {
  eventId?: string; // Jeśli jest, to edytujemy
  onSuccess?: (event: Event) => void;
  onCancel?: () => void;
}

type EventFormCreateDraft = {
  version: 1;
  updatedAt: number;
  formData: {
    title: string;
    description: string;
    start_date: string;
    start_time: string;
    end_date: string;
    end_time: string;
    timezone: string;
    is_virtual: boolean;
    location_name: string;
    location_address: string;
    location_city: string;
    location_country: string;
    meeting_link: string;
    meeting_password: string;
    organizer_website_link: string;
    is_free: boolean;
    price_amount: string;
    price_currency: string;
    price_description: string;
    max_attendees: string;
    category: string;
    tags: string;
    cover_image_url: string;
    status: 'draft' | 'published';
    organizers: Array<{ name: string; email: string; phone: string; bio: string; role: string; order: number }>;
  };
  selectedSpeakers: Array<{ author_id: string; role: string; order: number }>;
  showNewAuthorForm: boolean;
  newAuthor: { name: string; bio: string; website_url: string };
};

export const EventForm: React.FC<EventFormProps> = ({ eventId, onSuccess, onCancel }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(!!eventId);
  const [error, setError] = useState<string | null>(null);

  // Draft tylko dla /wydarzenia/nowe (create). sessionStorage przetrwa przełączanie kart.
  const CREATE_DRAFT_KEY = 'ws:event-form:draft:create';

  const readCreateDraft = (): EventFormCreateDraft | null => {
    if (typeof window === 'undefined') return null;
    try {
      const raw = window.sessionStorage.getItem(CREATE_DRAFT_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as EventFormCreateDraft;
      if (!parsed || parsed.version !== 1) return null;
      return parsed;
    } catch {
      return null;
    }
  };

  const clearCreateDraft = (): void => {
    if (typeof window === 'undefined') return;
    try {
      window.sessionStorage.removeItem(CREATE_DRAFT_KEY);
    } catch {
      // ignore
    }
  };
  
  // Authors and speakers
  const [authors, setAuthors] = useState<Author[]>([]);
  const [selectedSpeakers, setSelectedSpeakers] = useState<Array<{ author_id: string; role: string; order: number }>>([]);
  
  // New author form
  const [showNewAuthorForm, setShowNewAuthorForm] = useState(false);
  const [newAuthor, setNewAuthor] = useState({ name: '', bio: '', website_url: '' });
  const [creatingAuthor, setCreatingAuthor] = useState(false);

  // Files
  const [coverImageFile, setCoverImageFile] = useState<File | null>(null);
  const [coverImagePreview, setCoverImagePreview] = useState<string>('');

  const [formData, setFormData] = useState<{
    title: string;
    description: string;
    start_date: string;
    start_time: string;
    end_date: string;
    end_time: string;
    timezone: string;
    is_virtual: boolean;
    location_name: string;
    location_address: string;
    location_city: string;
    location_country: string;
    meeting_link: string;
    meeting_password: string;
    organizer_website_link: string;
    is_free: boolean;
    price_amount: string;
    price_currency: string;
    price_description: string;
    max_attendees: string;
    category: string;
    tags: string;
    cover_image_url: string;
    status: 'draft' | 'published';
    organizers: Array<{ name: string; email: string; phone: string; bio: string; role: string; order: number }>;
  }>({
    title: '',
    description: '',
    start_date: '',
    start_time: '',
    end_date: '',
    end_time: '',
    timezone: 'Europe/Warsaw',
    is_virtual: false,
    location_name: '',
    location_address: '',
    location_city: '',
    location_country: 'Poland',
    meeting_link: '',
    meeting_password: '',
    organizer_website_link: '',
    is_free: true,
    price_amount: '',
    price_currency: 'PLN',
    price_description: '',
    max_attendees: '',
    category: '',
    tags: '',
    cover_image_url: '',
    status: 'draft',
    organizers: [{ name: '', email: '', phone: '', bio: '', role: 'main', order: 0 }],
  });

  // Load authors list
  useEffect(() => {
    loadAuthors();
  }, []);

  // Autosave draft dla /wydarzenia/nowe (z debounce)
  useEffect(() => {
    if (eventId) return;
    if (typeof window === 'undefined') return;

    const timeout = window.setTimeout(() => {
      try {
        const draft: EventFormCreateDraft = {
          version: 1,
          updatedAt: Date.now(),
          formData,
          selectedSpeakers,
          showNewAuthorForm,
          newAuthor,
        };
        window.sessionStorage.setItem(CREATE_DRAFT_KEY, JSON.stringify(draft));
      } catch {
        // ignore
      }
    }, 300);

    return () => window.clearTimeout(timeout);
  }, [eventId, formData, selectedSpeakers, showNewAuthorForm, newAuthor]);

  // Load event data if editing
  useEffect(() => {
    if (eventId) {
      loadEventData();
    }
  }, [eventId]);

  // Przywróć draft na /wydarzenia/nowe
  useEffect(() => {
    if (eventId) return;
    const draft = readCreateDraft();
    if (!draft) return;

    setFormData(draft.formData);
    setSelectedSpeakers(draft.selectedSpeakers ?? []);
    setShowNewAuthorForm(Boolean(draft.showNewAuthorForm));
    setNewAuthor(draft.newAuthor ?? { name: '', bio: '', website_url: '' });
  }, [eventId]);

  const loadAuthors = async () => {
    try {
      const result = await getAllAuthors();
      if (result.success && result.data) {
        setAuthors(result.data);
      }
    } catch (error) {
      console.error('Error loading authors:', error);
    }
  };

  const loadEventData = async () => {
    if (!eventId) return;

    try {
      setLoadingData(true);
      const result = await getEventById(eventId);

      if (result.success && result.data) {
        const event = result.data;
        const startDate = event.start_date ? new Date(event.start_date) : null;
        const endDate = event.end_date ? new Date(event.end_date) : null;

        setFormData({
          title: event.title,
          description: event.description || '',
          start_date: startDate ? startDate.toISOString().split('T')[0] : '',
          start_time: startDate ? startDate.toTimeString().slice(0, 5) : '',
          end_date: endDate ? endDate.toISOString().split('T')[0] : '',
          end_time: endDate ? endDate.toTimeString().slice(0, 5) : '',
          timezone: event.timezone || 'Europe/Warsaw',
          is_virtual: event.is_virtual,
          location_name: event.location_name || '',
          location_address: event.location_address || '',
          location_city: event.location_city || '',
          location_country: event.location_country || 'Poland',
          meeting_link: event.meeting_link || '',
          meeting_password: event.meeting_password || '',
          organizer_website_link: event.organizer_website_link || '',
          is_free: event.is_free,
          price_amount: event.price_amount?.toString() || '',
          price_currency: event.price_currency || 'PLN',
          price_description: event.price_description || '',
          max_attendees: event.max_attendees?.toString() || '',
          category: event.category || '',
          tags: event.tags?.join(', ') || '',
          cover_image_url: event.cover_image_url || '',
          status: event.status,
          organizers:
            event.organizers && event.organizers.length > 0
              ? event.organizers.map((o) => ({
                  name: o.organizer_name,
                  email: o.organizer_email || '',
                  phone: o.organizer_phone || '',
                  bio: o.organizer_bio || '',
                  role: o.organizer_role || 'main',
                  order: o.organizer_order,
                }))
              : [{ name: '', email: '', phone: '', bio: '', role: 'main', order: 0 }],
        });

        // Załaduj prelegentów
        if (event.speakers && event.speakers.length > 0) {
          setSelectedSpeakers(
            event.speakers.map((s) => ({
              author_id: s.author_id,
              role: s.role || 'prelegent',
              order: s.speaker_order,
            }))
          );
        }
      } else {
        setError(result.error || 'Nie udało się załadować wydarzenia');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoadingData(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  // Speakers management
  const addSpeaker = (authorId: string) => {
    if (selectedSpeakers.some(s => s.author_id === authorId)) {
      return; // Already added
    }
    setSelectedSpeakers([
      ...selectedSpeakers,
      { author_id: authorId, role: 'prelegent', order: selectedSpeakers.length }
    ]);
  };

  const removeSpeaker = (authorId: string) => {
    setSelectedSpeakers(selectedSpeakers.filter(s => s.author_id !== authorId));
  };

  const updateSpeakerRole = (authorId: string, role: string) => {
    setSelectedSpeakers(selectedSpeakers.map(s => 
      s.author_id === authorId ? { ...s, role } : s
    ));
  };

  // New author management
  const handleCreateAuthor = async () => {
    if (!newAuthor.name.trim()) {
      alert('Imię i nazwisko jest wymagane');
      return;
    }

    setCreatingAuthor(true);
    try {
      const result = await createAuthor(
        newAuthor.name,
        newAuthor.bio || undefined,
        newAuthor.website_url || undefined
      );

      if (result.success && result.data) {
        // Add to authors list
        setAuthors([...authors, result.data]);
        
        // Auto-add as speaker
        addSpeaker(result.data.id);
        
        // Reset form
        setNewAuthor({ name: '', bio: '', website_url: '' });
        setShowNewAuthorForm(false);
        
        alert('Autor został dodany!');
      } else {
        alert(`Błąd: ${result.error || 'Nie udało się dodać autora'}`);
      }
    } catch (error: any) {
      alert(`Błąd: ${error.message}`);
    } finally {
      setCreatingAuthor(false);
    }
  };

  // File management
  const handleCoverImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setCoverImageFile(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setCoverImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeCoverImage = () => {
    setCoverImageFile(null);
    setCoverImagePreview('');
    setFormData({ ...formData, cover_image_url: '' });
  };

  const handleOrganizerChange = (index: number, field: string, value: string) => {
    const newOrganizers = [...formData.organizers];
    newOrganizers[index] = { ...newOrganizers[index], [field]: value };
    setFormData({ ...formData, organizers: newOrganizers });
  };

  const addOrganizer = () => {
    setFormData({
      ...formData,
      organizers: [
        ...formData.organizers,
        { name: '', email: '', phone: '', bio: '', role: 'co-organizer', order: formData.organizers.length },
      ],
    });
  };

  const removeOrganizer = (index: number) => {
    if (formData.organizers.length === 1) return; // Przynajmniej jeden organizator
    const newOrganizers = formData.organizers.filter((_, i) => i !== index);
    setFormData({ ...formData, organizers: newOrganizers });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      setError('Musisz być zalogowany aby dodać wydarzenie');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Upload cover image if provided
      let coverImageUrl = formData.cover_image_url;
      if (coverImageFile) {
        const uploadResult = await uploadEventFile(coverImageFile);
        if (uploadResult.success && uploadResult.data) {
          coverImageUrl = uploadResult.data.url;
        } else {
          setError(`Nie udało się przesłać grafiki: ${uploadResult.error}`);
          setLoading(false);
          return;
        }
      }

      // Combine date and time
      const startDateTime = formData.start_date && formData.start_time
        ? new Date(`${formData.start_date}T${formData.start_time}`)
        : undefined;
      const endDateTime = formData.end_date && formData.end_time
        ? new Date(`${formData.end_date}T${formData.end_time}`)
        : undefined;

      // Validation
      if (startDateTime && endDateTime && endDateTime <= startDateTime) {
        setError('Data końca musi być późniejsza niż data rozpoczęcia');
        setLoading(false);
        return;
      }

      // Prepare data
      const eventData: CreateEventData = {
        title: formData.title,
        description: formData.description || undefined,
        start_date: startDateTime?.toISOString(),
        end_date: endDateTime?.toISOString(),
        timezone: formData.timezone,
        is_virtual: formData.is_virtual,
        location_name: formData.location_name || undefined,
        location_address: formData.location_address || undefined,
        location_city: formData.location_city || undefined,
        location_country: formData.location_country || undefined,
        meeting_link: formData.meeting_link || undefined,
        meeting_password: formData.meeting_password || undefined,
        organizer_website_link: formData.organizer_website_link || undefined,
        is_free: formData.is_free,
        price_amount: formData.price_amount ? parseFloat(formData.price_amount) : undefined,
        price_currency: formData.price_currency || undefined,
        price_description: formData.price_description || undefined,
        max_attendees: formData.max_attendees ? parseInt(formData.max_attendees) : undefined,
        category: formData.category || undefined,
        tags: formData.tags
          ? formData.tags.split(',').map((tag) => tag.trim()).filter(Boolean)
          : undefined,
        cover_image_url: coverImageUrl || undefined,
        status: formData.status,
        speakers: selectedSpeakers.length > 0 ? selectedSpeakers : undefined,
        organizers: formData.organizers
          .filter((o) => o.name.trim())
          .map((o, index) => ({
            name: o.name.trim(),
            email: o.email.trim() || undefined,
            phone: o.phone.trim() || undefined,
            bio: o.bio.trim() || undefined,
            role: o.role || 'main',
            order: index,
          })),
      };

      let result;
      if (eventId) {
        result = await updateEvent({ id: eventId, ...eventData });
      } else {
        result = await createEvent(eventData, user.id);
      }

      if (result.success && result.data) {
        if (!eventId) {
          clearCreateDraft();
        }
        onSuccess?.(result.data);
      } else {
        setError(result.error || 'Wystąpił błąd podczas zapisywania wydarzenia');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <div className="event-form">
        <div className="event-form__loading">Ładowanie danych wydarzenia...</div>
      </div>
    );
  }

  return (
    <form className="event-form" onSubmit={handleSubmit}>
      <h2 className="event-form__title">
        {eventId ? 'Edytuj Wydarzenie' : 'Dodaj Nowe Wydarzenie'}
      </h2>

      {error && <div className="event-form__error">{error}</div>}

      {/* Basic Information */}
      <div className="event-form__section">
        <h3 className="event-form__section-title">Podstawowe Informacje</h3>

        <div className="event-form__field">
          <label htmlFor="title" className="event-form__label">
            Tytuł wydarzenia *
          </label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            className="event-form__input"
            required
          />
        </div>

        <div className="event-form__field">
          <label htmlFor="description" className="event-form__label">
            Opis
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            className="event-form__textarea"
            rows={5}
          />
        </div>

        <div className="event-form__field">
          <label htmlFor="category" className="event-form__label">
            Kategoria
          </label>
          <select
            id="category"
            name="category"
            value={formData.category}
            onChange={handleChange}
            className="event-form__select"
          >
            <option value="">Wybierz kategorię</option>
            <option value="szkolenie">Szkolenie</option>
            <option value="konferencja">Konferencja</option>
            <option value="warsztat">Warsztat</option>
            <option value="webinar">Webinar</option>
            <option value="spotkanie">Spotkanie</option>
            <option value="inne">Inne</option>
          </select>
        </div>

        <div className="event-form__field">
          <label htmlFor="tags" className="event-form__label">
            Tagi (rozdzielone przecinkami)
          </label>
          <input
            type="text"
            id="tags"
            name="tags"
            value={formData.tags}
            onChange={handleChange}
            className="event-form__input"
            placeholder="np. cybernetyka, szkolenie, online"
          />
        </div>

        <div className="event-form__field">
          <label htmlFor="cover_image_url" className="event-form__label">
            URL obrazka okładki
          </label>
          <input
            type="url"
            id="cover_image_url"
            name="cover_image_url"
            value={formData.cover_image_url}
            onChange={handleChange}
            className="event-form__input"
          />
        </div>
      </div>

      {/* Date & Time */}
      <div className="event-form__section">
        <h3 className="event-form__section-title">Data i Godzina</h3>

        <div className="event-form__row">
          <div className="event-form__field">
            <label htmlFor="start_date" className="event-form__label">
              Data rozpoczęcia
            </label>
            <input
              type="date"
              id="start_date"
              name="start_date"
              value={formData.start_date}
              onChange={handleChange}
              className="event-form__input"
            />
          </div>

          <div className="event-form__field">
            <label htmlFor="start_time" className="event-form__label">
              Godzina rozpoczęcia
            </label>
            <input
              type="time"
              id="start_time"
              name="start_time"
              value={formData.start_time}
              onChange={handleChange}
              className="event-form__input"
            />
          </div>
        </div>

        <div className="event-form__row">
          <div className="event-form__field">
            <label htmlFor="end_date" className="event-form__label">
              Data zakończenia
            </label>
            <input
              type="date"
              id="end_date"
              name="end_date"
              value={formData.end_date}
              onChange={handleChange}
              className="event-form__input"
            />
          </div>

          <div className="event-form__field">
            <label htmlFor="end_time" className="event-form__label">
              Godzina zakończenia
            </label>
            <input
              type="time"
              id="end_time"
              name="end_time"
              value={formData.end_time}
              onChange={handleChange}
              className="event-form__input"
            />
          </div>
        </div>

        <div className="event-form__field">
          <label htmlFor="timezone" className="event-form__label">
            Strefa czasowa
          </label>
          <select
            id="timezone"
            name="timezone"
            value={formData.timezone}
            onChange={handleChange}
            className="event-form__select"
          >
            <option value="Europe/Warsaw">Europa/Warszawa (CET/CEST)</option>
            <option value="UTC">UTC</option>
            <option value="Europe/London">Europa/Londyn (GMT/BST)</option>
            <option value="America/New_York">Ameryka/Nowy Jork (EST/EDT)</option>
          </select>
        </div>
      </div>

      {/* Location */}
      <div className="event-form__section">
        <h3 className="event-form__section-title">Lokalizacja</h3>
        <p className="event-form__hint">
          Wypełnij odpowiednie pola w zależności od typu wydarzenia (wirtualne, stacjonarne lub hybrydowe)
        </p>

        <div className="event-form__field">
          <label htmlFor="meeting_link" className="event-form__label">
            Link do spotkania online
          </label>
          <input
            type="url"
            id="meeting_link"
            name="meeting_link"
            value={formData.meeting_link}
            onChange={handleChange}
            className="event-form__input"
            placeholder="https://zoom.us/j/... lub https://meet.google.com/..."
          />
        </div>

        <div className="event-form__field">
          <label htmlFor="meeting_password" className="event-form__label">
            Hasło do spotkania (opcjonalne)
          </label>
          <input
            type="text"
            id="meeting_password"
            name="meeting_password"
            value={formData.meeting_password}
            onChange={handleChange}
            className="event-form__input"
            placeholder="Hasło dostępu"
          />
        </div>

        <div className="event-form__field">
          <label htmlFor="location_name" className="event-form__label">
            Nazwa miejsca stacjonarnego
          </label>
          <input
            type="text"
            id="location_name"
            name="location_name"
            value={formData.location_name}
            onChange={handleChange}
            className="event-form__input"
            placeholder="np. Hotel Marriott, Sala konferencyjna A"
          />
        </div>

        <div className="event-form__field">
          <label htmlFor="location_address" className="event-form__label">
            Adres
          </label>
          <input
            type="text"
            id="location_address"
            name="location_address"
            value={formData.location_address}
            onChange={handleChange}
            className="event-form__input"
            placeholder="ul. Przykładowa 1"
          />
        </div>

        <div className="event-form__row">
          <div className="event-form__field">
            <label htmlFor="location_city" className="event-form__label">
              Miasto
            </label>
            <input
              type="text"
              id="location_city"
              name="location_city"
              value={formData.location_city}
              onChange={handleChange}
              className="event-form__input"
              placeholder="np. Warszawa"
            />
          </div>

          <div className="event-form__field">
            <label htmlFor="location_country" className="event-form__label">
              Kraj
            </label>
            <input
              type="text"
              id="location_country"
              name="location_country"
              value={formData.location_country}
              onChange={handleChange}
              className="event-form__input"
              placeholder="np. Poland"
            />
          </div>
        </div>

        <div className="event-form__field">
          <label htmlFor="organizer_website_link" className="event-form__label">
            Link do strony organizatora
          </label>
          <input
            type="url"
            id="organizer_website_link"
            name="organizer_website_link"
            value={formData.organizer_website_link}
            onChange={handleChange}
            className="event-form__input"
          />
        </div>
      </div>

      {/* Pricing */}
      <div className="event-form__section">
        <h3 className="event-form__section-title">Cena</h3>

        <div className="event-form__field">
          <label className="event-form__checkbox-label">
            <input
              type="checkbox"
              name="is_free"
              checked={formData.is_free}
              onChange={handleChange}
              className="event-form__checkbox"
            />
            <span>Bezpłatne wydarzenie</span>
          </label>
        </div>

        {!formData.is_free && (
          <>
            <div className="event-form__row">
              <div className="event-form__field">
                <label htmlFor="price_amount" className="event-form__label">
                  Cena *
                </label>
                <input
                  type="number"
                  step="0.01"
                  id="price_amount"
                  name="price_amount"
                  value={formData.price_amount}
                  onChange={handleChange}
                  className="event-form__input"
                  required={!formData.is_free}
                />
              </div>

              <div className="event-form__field">
                <label htmlFor="price_currency" className="event-form__label">
                  Waluta
                </label>
                <select
                  id="price_currency"
                  name="price_currency"
                  value={formData.price_currency}
                  onChange={handleChange}
                  className="event-form__select"
                >
                  <option value="PLN">PLN</option>
                  <option value="EUR">EUR</option>
                  <option value="USD">USD</option>
                  <option value="GBP">GBP</option>
                </select>
              </div>
            </div>

            <div className="event-form__field">
              <label htmlFor="price_description" className="event-form__label">
                Dodatkowe informacje o cenie
              </label>
              <input
                type="text"
                id="price_description"
                name="price_description"
                value={formData.price_description}
                onChange={handleChange}
                className="event-form__input"
                placeholder="np. Zniżka dla studentów: 50 PLN"
              />
            </div>
          </>
        )}

        <div className="event-form__field">
          <label htmlFor="max_attendees" className="event-form__label">
            Maksymalna liczba uczestników (opcjonalne)
          </label>
          <input
            type="number"
            id="max_attendees"
            name="max_attendees"
            value={formData.max_attendees}
            onChange={handleChange}
            className="event-form__input"
            placeholder="Pozostaw puste dla bez limitu"
          />
        </div>
      </div>

      {/* Cover Image / Plakat */}
      <div className="event-form__section">
        <h3 className="event-form__section-title">Plakat / Grafika Wydarzenia</h3>
        <p className="event-form__hint">
          Dodaj plakat, grafikę lub okładkę wydarzenia (opcjonalne)
        </p>

        {coverImagePreview || formData.cover_image_url ? (
          <div className="event-form__file-preview">
            <img 
              src={coverImagePreview || formData.cover_image_url} 
              alt="Podgląd okładki"
              className="event-form__image-preview"
            />
            <button
              type="button"
              onClick={removeCoverImage}
              className="event-form__btn-remove-file"
            >
              Usuń grafikę
            </button>
          </div>
        ) : (
          <div className="event-form__field">
            <label htmlFor="cover_image" className="event-form__label">
              Wybierz plik graficzny
            </label>
            <input
              type="file"
              id="cover_image"
              accept="image/*"
              onChange={handleCoverImageChange}
              className="event-form__file-input"
            />
            <small className="event-form__hint" style={{ marginTop: '0.5rem' }}>
              Obsługiwane formaty: JPG, PNG, WEBP (maks. 5MB)
            </small>
          </div>
        )}

        <div className="event-form__field">
          <label htmlFor="cover_image_url" className="event-form__label">
            Lub podaj link do grafiki
          </label>
          <input
            type="url"
            id="cover_image_url"
            name="cover_image_url"
            value={formData.cover_image_url}
            onChange={handleChange}
            className="event-form__input"
            placeholder="https://..."
            disabled={!!coverImageFile}
          />
        </div>
      </div>

      {/* Speakers (Prelegenci) */}
      <div className="event-form__section">
        <h3 className="event-form__section-title">Prelegenci</h3>
        <p className="event-form__hint">
          Wybierz autorów którzy będą prelegentami na tym wydarzeniu
        </p>

        {selectedSpeakers.length > 0 && (
          <div className="event-form__speakers-list">
            {selectedSpeakers.map((speaker) => {
              const author = authors.find(a => a.id === speaker.author_id);
              return (
                <div key={speaker.author_id} className="event-form__speaker-item">
                  <div className="event-form__speaker-info">
                    <strong>{author?.name}</strong>
                    {author?.bio && <small>{author.bio.substring(0, 100)}...</small>}
                  </div>
                  <div className="event-form__speaker-controls">
                    <select
                      value={speaker.role}
                      onChange={(e) => updateSpeakerRole(speaker.author_id, e.target.value)}
                      className="event-form__select-small"
                    >
                      <option value="prelegent">Prelegent</option>
                      <option value="moderator">Moderator</option>
                      <option value="gość specjalny">Gość specjalny</option>
                      <option value="współprowadzący">Współprowadzący</option>
                    </select>
                    <button
                      type="button"
                      onClick={() => removeSpeaker(speaker.author_id)}
                      className="event-form__btn-remove-small"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="event-form__field">
          <label htmlFor="speaker-select" className="event-form__label">
            Dodaj prelegenta
          </label>
          <select
            id="speaker-select"
            onChange={(e) => {
              if (e.target.value) {
                addSpeaker(e.target.value);
                e.target.value = '';
              }
            }}
            className="event-form__select"
          >
            <option value="">Wybierz autora...</option>
            {authors
              .filter(author => !selectedSpeakers.some(s => s.author_id === author.id))
              .map(author => (
                <option key={author.id} value={author.id}>
                  {author.name}
                </option>
              ))}
          </select>
        </div>

        {/* New Author Form */}
        {!showNewAuthorForm ? (
          <button
            type="button"
            onClick={() => setShowNewAuthorForm(true)}
            className="event-form__btn-add-author"
          >
            + Utwórz Nowego Autora
          </button>
        ) : (
          <div className="event-form__new-author">
            <h4 className="event-form__new-author-title">Dodaj Nowego Autora</h4>
            
            <div className="event-form__field">
              <label htmlFor="new-author-name" className="event-form__label">
                Imię i nazwisko *
              </label>
              <input
                type="text"
                id="new-author-name"
                value={newAuthor.name}
                onChange={(e) => setNewAuthor({ ...newAuthor, name: e.target.value })}
                className="event-form__input"
                placeholder="np. Jan Kowalski"
              />
            </div>

            <div className="event-form__field">
              <label htmlFor="new-author-bio" className="event-form__label">
                Biografia (opcjonalna)
              </label>
              <textarea
                id="new-author-bio"
                value={newAuthor.bio}
                onChange={(e) => setNewAuthor({ ...newAuthor, bio: e.target.value })}
                className="event-form__textarea"
                placeholder="Krótka biografia autora..."
                rows={3}
              />
            </div>

            <div className="event-form__field">
              <label htmlFor="new-author-website" className="event-form__label">
                Strona WWW (opcjonalna)
              </label>
              <input
                type="url"
                id="new-author-website"
                value={newAuthor.website_url}
                onChange={(e) => setNewAuthor({ ...newAuthor, website_url: e.target.value })}
                className="event-form__input"
                placeholder="https://..."
              />
            </div>

            <div className="event-form__new-author-actions">
              <button
                type="button"
                onClick={handleCreateAuthor}
                disabled={creatingAuthor || !newAuthor.name.trim()}
                className="event-form__btn-create-author"
              >
                {creatingAuthor ? 'Tworzenie...' : 'Utwórz i Dodaj jako Prelegenta'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowNewAuthorForm(false);
                  setNewAuthor({ name: '', bio: '', website_url: '' });
                }}
                className="event-form__btn-cancel-author"
              >
                Anuluj
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Organizers */}
      <div className="event-form__section">
        <h3 className="event-form__section-title">Organizatorzy</h3>

        {formData.organizers.map((organizer, index) => (
          <div key={index} className="event-form__organizer">
            <div className="event-form__organizer-header">
              <h4>Organizator {index + 1}</h4>
              {formData.organizers.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeOrganizer(index)}
                  className="event-form__btn-remove"
                >
                  Usuń
                </button>
              )}
            </div>

            <div className="event-form__field">
              <label htmlFor={`organizer-name-${index}`} className="event-form__label">
                Imię i nazwisko *
              </label>
              <input
                type="text"
                id={`organizer-name-${index}`}
                value={organizer.name}
                onChange={(e) => handleOrganizerChange(index, 'name', e.target.value)}
                className="event-form__input"
                required
              />
            </div>

            <div className="event-form__row">
              <div className="event-form__field">
                <label htmlFor={`organizer-email-${index}`} className="event-form__label">
                  Email
                </label>
                <input
                  type="email"
                  id={`organizer-email-${index}`}
                  value={organizer.email}
                  onChange={(e) => handleOrganizerChange(index, 'email', e.target.value)}
                  className="event-form__input"
                />
              </div>

              <div className="event-form__field">
                <label htmlFor={`organizer-phone-${index}`} className="event-form__label">
                  Telefon
                </label>
                <input
                  type="tel"
                  id={`organizer-phone-${index}`}
                  value={organizer.phone}
                  onChange={(e) => handleOrganizerChange(index, 'phone', e.target.value)}
                  className="event-form__input"
                />
              </div>
            </div>

            <div className="event-form__field">
              <label htmlFor={`organizer-role-${index}`} className="event-form__label">
                Rola
              </label>
              <select
                id={`organizer-role-${index}`}
                value={organizer.role}
                onChange={(e) => handleOrganizerChange(index, 'role', e.target.value)}
                className="event-form__select"
              >
                <option value="main">Główny organizator</option>
                <option value="co-organizer">Współorganizator</option>
                <option value="sponsor">Sponsor</option>
                <option value="partner">Partner</option>
              </select>
            </div>

            <div className="event-form__field">
              <label htmlFor={`organizer-bio-${index}`} className="event-form__label">
                Bio (opcjonalne)
              </label>
              <textarea
                id={`organizer-bio-${index}`}
                value={organizer.bio}
                onChange={(e) => handleOrganizerChange(index, 'bio', e.target.value)}
                className="event-form__textarea"
                rows={2}
              />
            </div>
          </div>
        ))}

        <button type="button" onClick={addOrganizer} className="event-form__btn-add">
          + Dodaj Organizatora
        </button>
      </div>

      {/* Status */}
      <div className="event-form__section">
        <h3 className="event-form__section-title">Status</h3>

        <div className="event-form__field">
          <label htmlFor="status" className="event-form__label">
            Status publikacji
          </label>
          <select
            id="status"
            name="status"
            value={formData.status}
            onChange={handleChange}
            className="event-form__select"
          >
            <option value="draft">Szkic</option>
            <option value="published">Opublikowane</option>
          </select>
        </div>
      </div>

      {/* Actions */}
      <div className="event-form__actions">
        {!eventId && (
          <button
            type="button"
            onClick={() => {
              const shouldClear = confirm('Usunąć niezapisany szkic formularza?');
              if (!shouldClear) return;

              clearCreateDraft();
              setError(null);
              setSelectedSpeakers([]);
              setShowNewAuthorForm(false);
              setNewAuthor({ name: '', bio: '', website_url: '' });
              setCoverImageFile(null);
              setCoverImagePreview('');
              setFormData({
                title: '',
                description: '',
                start_date: '',
                start_time: '',
                end_date: '',
                end_time: '',
                timezone: 'Europe/Warsaw',
                is_virtual: false,
                location_name: '',
                location_address: '',
                location_city: '',
                location_country: 'Poland',
                meeting_link: '',
                meeting_password: '',
                organizer_website_link: '',
                is_free: true,
                price_amount: '',
                price_currency: 'PLN',
                price_description: '',
                max_attendees: '',
                category: '',
                tags: '',
                cover_image_url: '',
                status: 'draft',
                organizers: [{ name: '', email: '', phone: '', bio: '', role: 'main', order: 0 }],
              });
            }}
            className="event-form__btn-cancel"
          >
            Wyczyść szkic
          </button>
        )}
        {onCancel && (
          <button type="button" onClick={onCancel} className="event-form__btn-cancel">
            Anuluj
          </button>
        )}
        <button type="submit" disabled={loading} className="event-form__btn-submit">
          {loading ? 'Zapisywanie...' : eventId ? 'Zaktualizuj Wydarzenie' : 'Dodaj Wydarzenie'}
        </button>
      </div>
    </form>
  );
};

